import { useEffect, useState, useRef, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import { formatDate } from "../../utils/helpers";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MobileChatPanel({ jobOrderId, onClose, onCountUpdate, notifRef }) {
  const { user, hasPerm } = useAuth();
  const canPost = hasPerm("chat.post");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);
  const userScrolledUp = useRef(false);
  const prevMsgCount = useRef(0);

  const fetchMessages = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/job-orders/${jobOrderId}/messages`);
      setData(res);
      if (onCountUpdate) onCountUpdate(jobOrderId, res.messages.length);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [jobOrderId, onCountUpdate]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Poll the SSE notifRef for events matching this job order
  useEffect(() => {
    if (!notifRef) return;
    const interval = setInterval(() => {
      const ev = notifRef.current;
      if (ev && ev.job_order_id === jobOrderId) {
        notifRef.current = null;
        fetchMessages();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [notifRef, jobOrderId, fetchMessages]);

  // Track whether user has scrolled up
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const handleScroll = () => {
      const threshold = 150;
      userScrolledUp.current = el.scrollHeight - el.scrollTop - el.clientHeight > threshold;
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll to bottom only on initial load, user's own send, or if already at bottom
  useEffect(() => {
    if (!threadRef.current || !data?.messages) return;
    const isInitialLoad = prevMsgCount.current === 0;
    prevMsgCount.current = data.messages.length;
    if (isInitialLoad || !userScrolledUp.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [data?.messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    userScrolledUp.current = false;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/job-orders/${jobOrderId}/messages`, {
        message_text: text.trim(),
      });
      setText("");
      setData((prev) => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
      if (onCountUpdate && data) onCountUpdate(jobOrderId, (data.messages?.length || 0) + 1);
    } catch {
      // ignore
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="bg-blue-700 text-white px-4 py-3 flex items-center gap-3 shrink-0">
        <button
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-blue-600 active:bg-blue-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">
            {data?.jo_number || `JO #${jobOrderId}`}
          </p>
          <p className="text-xs text-blue-200 truncate">
            {data?.customer_full_name}
            {data?.job_date && ` · ${formatDate(data.job_date)}`}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div ref={threadRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-gray-50">
        {loading ? (
          <p className="text-center text-sm text-gray-400 py-12">Loading…</p>
        ) : data?.messages?.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-12">No messages yet.</p>
        ) : (
          data?.messages?.map((msg) => {
            const isOwn = msg.posted_by_user_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                  isOwn
                    ? "bg-blue-600 text-white rounded-br-sm"
                    : "bg-white text-gray-900 border rounded-bl-sm"
                }`}>
                  {!isOwn && (
                    <p className="text-xs font-semibold text-blue-700 mb-0.5">
                      {msg.posted_by_name}
                      {msg.posted_by_role && (
                        <span className="font-normal text-gray-400 ml-1">· {msg.posted_by_role}</span>
                      )}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.message_text}</p>
                  <p className={`text-[10px] mt-0.5 ${isOwn ? "text-blue-200" : "text-gray-400"}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input */}
      {canPost ? (
        <div className="px-3 py-2 border-t bg-white flex gap-2 shrink-0 safe-area-pb">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSend(); } }}
            placeholder="Type a message…"
            className="flex-1 border rounded-full px-4 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white disabled:opacity-40 active:bg-blue-700 shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="px-3 py-3 border-t text-center text-xs text-gray-400 shrink-0">
          You do not have permission to post messages.
        </div>
      )}
    </div>
  );
}
